import { useToast } from '@chakra-ui/react';
import { AxiosResponse } from 'axios';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { useHistory } from 'react-router';
import { getCompanies, getCompanyById, updateCompanyById } from '~/services';
import { CompanyView, Pagination } from '~/types';
import { queryClient } from '~/utils/react-query';
import { UpdatePayload } from './types';
import { PAGE_SIZE } from '~/constants';

export function useCompanies(
  page: number,
  search = '',
  orderby = 'nm_grupoeconomico',
  order = 'ASC',
  options: UseQueryOptions<Pagination<CompanyView>, unknown> = {},
  page_size = PAGE_SIZE,
): UseQueryResult<Pagination<CompanyView>, unknown> {
  return useQuery<Pagination<CompanyView>, unknown>(
    ['companies', page, search, order, page_size],
    () => getCompanies(page, search, orderby, order, page_size),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useCompany(
  id: number,
  options: UseQueryOptions<CompanyView, unknown> = {},
): UseQueryResult<CompanyView> {
  const toast = useToast();
  const history = useHistory();

  return useQuery<CompanyView, unknown>(
    ['company', id],
    () => getCompanyById(id),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro buscar detalhes da empresa',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        history.goBack();
      },
    },
  );
}

export function useUpdateCompany(): UseMutationResult<
  AxiosResponse<any>,
  unknown,
  UpdatePayload,
  unknown
> {
  const toast = useToast();

  return useMutation(
    async (data: { id: number; formData: any }) =>
      updateCompanyById(data.id, data.formData),
    {
      onSuccess: () => {
        toast({
          title: `Empresa atualizada`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        queryClient.invalidateQueries('companies');
        queryClient.invalidateQueries('company');
      },
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro ao atualizar a empresa, tente novamente',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}
